import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import '../components/Navbar.css';
import '../components/HeroCards.css';
import '../components/AdminTables.css';

function CandidatesPage() {
  const navigate = useNavigate();
  

  // State for options (fetched for elections, dynamic for offices)
  const [electionOptions, setElectionOptions] = useState([]);  // Fetched from API
  const [officeOptions, setOfficeOptions] = useState([]); // Will be derived from candidates for selected election

  // Form state
  const [selectedElection, setSelectedElection] = useState('');
  const [selectedOffice, setSelectedOffice] = useState('GOV');
  const [loadingElections, setLoadingElections] = useState(true);
  const [electionError, setElectionError] = useState(null);
  const [newest, setNewest] = useState([]);
  const [newestBalances, setNewestBalances] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const columnWidths = {
    account: '80px',
    name: '200px',
    office: '220px',
    district: '60px',
    coh: '140px'
  };

  // Fetch election options from API
  useEffect(() => {
    const fetchElections = async () => {
      try {
        setLoadingElections(true);
        setElectionError(null);
        const response = await fetch('/api/electioncodes');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const allCodes = await response.json();
        
        // Filter for general elections (name ends with "Election")
        const filteredElections = allCodes
          .filter(code => code.name && code.name.endsWith('Election'))
          .map(code => ({ value: code.value, label: code.name }))
          .sort((a, b) => new Date(b.value) - new Date(a.value));  // Sort descending by date (newest first)
        
        setElectionOptions(filteredElections);
        // Prefer a 2026 election code if present, otherwise default to newest
        if (filteredElections.length > 0) {
          const found2026 = filteredElections.find(fe => String(fe.value).includes('2026'));
          setSelectedElection(found2026 ? found2026.value : filteredElections[0].value);
        }
      } catch (err) {
        console.error('Fetch elections error:', err);
        setElectionError(err.message);
        // Fallback to hardcoded
        setElectionOptions([
          { value: '20181106-GEN', label: '2018 General Election' },
          { value: '20261103-GEN', label: '2026 General Election' }
        ]);
        setSelectedElection('20261103-GEN');
      } finally {
        setLoadingElections(false);
      }
    };

    fetchElections();
    // Also fetch newest 15 candidates
    const fetchNewest = async () => {
      try {
        const res = await fetch('/api/candidates?sort=acctnum_desc&limit=15');
        if (res.ok) {
          const list = await res.json();
          setNewest(list);
          // Collect acctNums including AccountList entries
          const acctNumsFromMain = list.map(c => c.AcctNum).filter(n => n != null).map(String);
          const acctNumsFromAccountList = [];
          list.forEach(c => {
            if (Array.isArray(c.AccountList)) c.AccountList.forEach(a => { if (a && a.accountNum != null) acctNumsFromAccountList.push(String(a.accountNum)); });
          });
          const allAcctNums = Array.from(new Set([...acctNumsFromMain, ...acctNumsFromAccountList]));
          if (allAcctNums.length > 0) {
            const br = await fetch(`/api/balances?accounts=${allAcctNums.join(',')}`);
            if (br.ok) setNewestBalances(await br.json());
          }
        }
      } catch (err) {
        console.error('Error fetching newest candidates', err);
      }
    };
    fetchNewest();
  }, []);

  // When selectedElection changes, fetch candidates for that election and derive unique office options
  useEffect(() => {
    const fetchOfficesForElection = async () => {
      if (!selectedElection) return;
      try {
        const res = await fetch(`/api/candidates/election/${encodeURIComponent(selectedElection)}`);
        if (!res.ok) {
          console.warn('Failed to fetch candidates for election to derive offices:', res.status);
          setOfficeOptions([]);
          return;
        }
        const candidates = await res.json();
        // Build map of OfficeCode -> OfficeDesc (prefer OfficeDesc when available)
        const officeMap = new Map();
        (candidates || []).forEach(c => {
          const code = c.OfficeCode || c.Office || '';
          const desc = c.OfficeDesc || c.OfficeDescription || c.Office || code;
          if (code) {
            if (!officeMap.has(code)) officeMap.set(code, desc || code);
          }
        });
        const opts = Array.from(officeMap.entries()).map(([value, label]) => ({ value, label }));
        // Sort options by label
        opts.sort((a, b) => a.label.localeCompare(b.label));
        setOfficeOptions(opts);
        // If current selectedOffice is not in opts, pick first
        if (!opts.find(o => o.value === selectedOffice)) {
          if (opts.length > 0) setSelectedOffice(opts[0].value);
        }
      } catch (err) {
        console.error('Error deriving office options:', err);
        setOfficeOptions([]);
      }
    };

    fetchOfficesForElection();
  }, [selectedElection]);

  // Handle form submit: Navigate to results page
  const handleSubmit = (e) => {
    e.preventDefault();
    const election = selectedElection || electionOptions[0]?.value || '20181106-GEN';
    const office = selectedOffice || 'STS';
    navigate(`/candidates/${election}/${office}`);
  };

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', color: '#000' }}>
      <Navbar />

  <main style={{ maxWidth: '900px', margin: '8px auto', padding: '12px' }}>
        <section style={{ padding: '10px 12px', borderRadius: 12, boxShadow: '0 1px 6px rgba(16,24,40,0.04)' }}>
          <h2 style={{ marginTop: 0, marginBottom: 8 }}>Search Florida Election Candidates</h2>
          <p>Select an election and office to view candidates.</p>

          <form onSubmit={handleSubmit} style={{ marginTop: '20px', padding: '20px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Election:</label>
          {loadingElections ? (
            <select disabled style={{ width: '100%', padding: '8px', fontSize: '16px' }}>
              <option>Loading elections...</option>
            </select>
          ) : electionError ? (
            <p style={{ color: 'red' }}>Error loading elections: {electionError}</p>
          ) : (
            <select 
              value={selectedElection} 
              onChange={(e) => setSelectedElection(e.target.value)} 
              style={{ width: '100%', padding: '8px', fontSize: '16px' }}
            >
              {electionOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Office:</label>
          <select 
            value={selectedOffice} 
            onChange={(e) => setSelectedOffice(e.target.value)} 
            style={{ width: '100%', padding: '8px', fontSize: '16px' }}
          >
            {officeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        
        <button 
          type="submit" 
          disabled={loadingElections}  // Disable if fetching
          style={{ 
            width: '100%', 
            padding: '10px', 
            backgroundColor: loadingElections ? '#ccc' : '#007bff', 
            color: 'white', 
            border: 'none', 
            borderRadius: '5px', 
            fontSize: '16px',
            cursor: loadingElections ? 'not-allowed' : 'pointer'
          }}
        >
          {loadingElections ? 'Loading...' : 'Load Candidates'}
        </button>
          </form>
          {/* Newest candidates table */}
          <div style={{ marginTop: 20 }}>
            <h3>Latest Candidate Filings</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Name</th>
                    <th>Office</th>
                    <th>District</th>
                    <th style={{ textAlign: 'right' }}>Cash on Hand</th>
                  </tr>
                </thead>
                <tbody>
                  {newest.length > 0 ? newest.map((c, i) => {
                    const balMap = new Map((newestBalances || []).map(b => [String(b.account).trim(), b.coh]));
                    // build acct list
                    const acctList = Array.isArray(c.AccountList) && c.AccountList.length > 0 ? c.AccountList.map(a => ({ accountNum: String(a.accountNum).trim(), accountName: a.accountName })) : [{ accountNum: String(c.AcctNum).trim(), accountName: 'Campaign' }];
                    const sumCoh = acctList.reduce((s, a) => {
                      const v = balMap.get(a.accountNum);
                      return s + (v != null && !isNaN(Number(v)) ? Number(v) : 0);
                    }, 0);
                    const hasMultiple = acctList.length > 1;
                    const idKey = c._id || c.AcctNum || i;
                    const isExpanded = expandedRows.has(String(idKey));

                    const toggle = () => {
                      const next = new Set(expandedRows);
                      const key = String(idKey);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      setExpandedRows(next);
                    };

                    // No alternating background — keep consistent row background
                    const rowStyle = {};

                    return (
                      <React.Fragment key={idKey}>
                        <tr style={rowStyle}>
                          <td style={{ width: columnWidths.account, padding: '8px' }}>
                            {hasMultiple ? <a onClick={toggle} style={{ cursor: 'pointer', marginRight: 8, color: '#444' }}>{isExpanded ? '▾' : '▸'}</a> : <span style={{ display: 'inline-block', width: 14, marginRight: 8 }} />}
                            <Link to={`/candidates/${c.AcctNum}`} style={{ color: '#007bff', textDecoration: 'none' }}>{c.AcctNum}</Link>
                          </td>
                          <td style={{ width: columnWidths.name, padding: '4px' }}>{(c.NameFirst || '') + ' ' + (c.NameLast || '')}</td>
                          <td style={{ width: columnWidths.office, padding: '4px' }}>{c.OfficeDesc || c.OfficeCode}</td>
                          <td style={{ width: columnWidths.district, padding: '4px' }}>{c.Juris1num || ''}</td>
                          <td style={{ width: columnWidths.coh, padding: '4px', textAlign: 'right' }}>{sumCoh ? `$${Number(sumCoh).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</td>
                        </tr>

                        {isExpanded && hasMultiple && (
                          <tr>
                            <td colSpan={5} style={{ padding: 8, background: '#fafafa', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                              <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}>
                                  <strong>Account breakdown</strong>
                                  <div style={{ marginTop: 8 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr style={{ textAlign: 'left', color: '#333' }}>
                                          <th style={{ width: 140, padding: 6 }}>Account</th>
                                          <th style={{ padding: 6 }}>Name</th>
                                          <th style={{ width: 160, padding: 6, textAlign: 'right' }}>COH</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {acctList.map((a, ai) => (
                                          <tr key={ai}>
                                            <td style={{ padding: 6 }}>{a.accountNum}</td>
                                            <td style={{ padding: 6 }}>{a.accountName}</td>
                                            <td style={{ padding: 6, textAlign: 'right' }}>{balMap.get(a.accountNum) != null ? `$${Number(balMap.get(a.accountNum)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }) : (
                    <tr><td colSpan="4" style={{ padding: 12, textAlign: 'center' }}>No recent candidates found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default CandidatesPage;